import { formatDate, DATE_US } from '@koob/dates';
import { buildPricingDisplay, getPricingItemAmount } from '@koob/utils';

export const CATEGORY_KEY_MAP = {
  hotels: 'hotels',
  experiences: 'experiences',
  'additional-services': 'additionalServices',
};

export const MODE_MAP = {
  total: 'global',
  category: 'categories',
  itemized: 'items',
};

function normalizeService(service, rate) {
  const rawPrice = Number(service.totalPrice ?? service.price ?? 0);
  return {
    ...service,
    type: service.kind,
    price: rate ? Math.round(rawPrice * rate) : rawPrice,
    markup: service.markup ?? service.margin ?? 0,
    isMarkupPercent:
      service.isMarkupPercent ?? service.isMarginPercent ?? false,
  };
}

function formatItemDate(value) {
  return value ? formatDate(value, DATE_US) : null;
}

export default function getTripPricing(trip, pricingSummary) {
  if (!pricingSummary?.services?.length) return null;

  if (pricingSummary.isTravelDiaryPriceDisplayed === false) return null;

  const rate =
    pricingSummary.targetCurrency && pricingSummary.exchangeRate
      ? Number(pricingSummary.exchangeRate)
      : null;
  const currency = rate
    ? pricingSummary.targetCurrency
    : (pricingSummary.services?.[0]?.currency ??
      pricingSummary.currency ??
      'EUR');

  const displayMode = pricingSummary.travelDiaryPriceDisplayLevel ?? 'itemized';
  const display = buildPricingDisplay({
    services: pricingSummary.services.map((service) =>
      normalizeService(service, rate),
    ),
    markup: pricingSummary.margin ?? 0,
    isMarkupPercent: pricingSummary.isMarginPercent ?? false,
    displayMode,
  });

  const travellerCount =
    (trip?.adultPax ?? 0) + (trip?.childrenBirthdates?.length ?? 0) ||
    trip?.maxPax ||
    1;
  const perPerson = (amount) =>
    travellerCount > 0 ? amount / travellerCount : amount;

  return {
    mode: MODE_MAP[displayMode] ?? 'items',
    currency,
    travellerCount,
    perPersonTotal: perPerson(display.total),
    grandTotal: display.total,
    categories: display.categories.map((category) => ({
      key: CATEGORY_KEY_MAP[category.id] ?? category.id,
      perPersonAmount: perPerson(category.subtotal),
    })),
    items: display.categories.flatMap((category) =>
      category.items.map((item) => ({
        category: CATEGORY_KEY_MAP[category.id] ?? category.id,
        label: item.name,
        startDate: formatItemDate(item.startAt),
        endDate: formatItemDate(item.endAt),
        pax: item.pax ?? travellerCount,
        amount: getPricingItemAmount(item),
      })),
    ),
  };
}
